/* eslint-disable react-refresh/only-export-components -- `useScopeFilter` is the control's
   contract, not a helper that happens to live here: the hook owns the cascade, the resolved user
   ids and the scope sentence, and the component renders exactly that state. Splitting them is how
   the chips on screen and the ids in the request stop agreeing. Fast Refresh gives up on this file. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import Select from '../ui/Select';
import { cn } from '../../utils/cn';
import { FetchUsers } from '../../api/General/account';
import { getBranches, getDepartments } from '../../api/General/Company';
import { getUsersCountries } from '../../api/General/Country';

/**
 * WHO a report is about — Country → Branch → Department → People, cascading.
 *
 * Lifted out of `components/reportsController` (which stays where it is until the other attendance
 * tabs migrate) with two deliberate changes:
 *
 *  • **The AND/OR toggle is gone.** It offered a choice nobody could act on: `AND` narrowed the
 *    roster and auto-selected the survivors, while `OR` silently threw the whole scope away and
 *    reset the people picker to empty (reportsController's `filterData`, `else` branch). Two
 *    controls, one of which cancelled the other, on a filter whose whole job is to say who the
 *    figures are about. Scope here is always the intersection, which is the only reading the word
 *    "scope" supports.
 *
 *  • **`resolvedUserIds` is a first-class output**, because the endpoints this feeds
 *    (`reports/Overtime`) take `UserIds` and nothing else. The old controller left every consumer
 *    to re-derive that mapping, and they did it differently.
 *
 * 🔴 AN EMPTY `resolvedUserIds` MEANS "EVERYONE THE SERVER LETS ME SEE", NOT "NOBODY".
 * `ReportsController.ApplyOvertimeUserScopeAsync` expands an empty `UserIds` to
 * `GetCompanyUserIds(caller)` — Hr/Chief get the company, a Manager gets their team, everyone else
 * gets themselves. That is exactly right for the "All employees" default and exactly WRONG for a
 * scope that matched nobody, which is why `hasScope` is exported alongside it: a caller must not
 * send an empty list on behalf of a scope the user actually set. See `matchedNobody`.
 *
 * The roster is also the cascade's data source. Branch and department lists come from the company
 * (so a branch with nobody in it is still offered before any country is picked) but are narrowed to
 * what the people in scope actually belong to as soon as a level above them is chosen — offering a
 * department that cannot contain anybody is a dead option on a filter, and the reader reads a dead
 * option as a bug in the data.
 */

export interface ScopeValue {
  countryIds: number[];
  branchIds: number[];
  departmentIds: number[];
  /** Explicitly picked people. Overrides the levels above it — see `resolvedUserIds`. */
  userIds: string[];
}

export const EMPTY_SCOPE: ScopeValue = Object.freeze({
  countryIds: [],
  branchIds: [],
  departmentIds: [],
  userIds: [],
}) as ScopeValue;

export interface ScopeOption {
  id: number;
  name: string;
}

export interface ScopePerson {
  id: string;
  name: string;
  countryId?: number;
  branchId?: number;
  departmentId?: number;
}

export interface ScopeFilterState {
  value: ScopeValue;
  setCountryIds: (ids: number[]) => void;
  setBranchIds: (ids: number[]) => void;
  setDepartmentIds: (ids: number[]) => void;
  setUserIds: (ids: string[]) => void;
  /** Back to "All employees". */
  reset: () => void;

  /** The options each level currently offers, already narrowed by the levels above it. */
  countries: ScopeOption[];
  branches: ScopeOption[];
  departments: ScopeOption[];
  people: ScopePerson[];
  /** The WHOLE company roster, unnarrowed — a caller resolving a name by id must not lose the
   *  person the moment the scope excludes them (the viewer's own name, typically). */
  roster: ScopePerson[];

  /** True once the user has narrowed anything at all. */
  hasScope: boolean;
  /** A scope IS set and it matches nobody — the caller must render an empty report, not query. */
  matchedNobody: boolean;
  /**
   * The `UserIds` to send. Empty when nothing is scoped, which the server reads as "everyone I am
   * allowed to see" — never as "nobody". Guard with `matchedNobody`.
   */
  resolvedUserIds: string[];
  /** One line naming the scope, for the filter chip and the export's scope label. */
  scopeText: string;
  loading: boolean;
  reload: () => void;
}

interface UseScopeFilterOptions {
  companyId: number | undefined;
  /** The viewer, for `Country/GetUsersCountries` — the countries their roster actually spans. */
  userId: string | undefined;
  /** Skip loading entirely (a page that has not been opened yet). */
  enabled?: boolean;
}

/** Ids of a set, for the `some` tests below, tolerating the `undefined` a person without one has. */
const inSet = (ids: number[], value: number | undefined) =>
  ids.length === 0 || (value !== undefined && ids.includes(value));

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/** Distinct, named options in name order — the same option arriving on 40 people is still one. */
function distinct(options: (ScopeOption | undefined)[]): ScopeOption[] {
  const map = new Map<number, ScopeOption>();
  for (const option of options) {
    if (option && option.id > 0 && option.name && !map.has(option.id)) map.set(option.id, option);
  }
  return Array.from(map.values()).sort(byName);
}

export function useScopeFilter({
  companyId,
  userId,
  enabled = true,
}: UseScopeFilterOptions): ScopeFilterState {
  const { t } = useTranslation();

  const [value, setValue] = useState<ScopeValue>(EMPTY_SCOPE);
  const [roster, setRoster] = useState<ScopePerson[]>([]);
  const [allCountries, setAllCountries] = useState<ScopeOption[]>([]);
  const [allBranches, setAllBranches] = useState<ScopeOption[]>([]);
  const [allDepartments, setAllDepartments] = useState<ScopeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      // Four independent reads, in parallel and each failing on its own: a company with no branches
      // configured must not cost the report its people picker.
      const [users, countries, branches, departments] = await Promise.all([
        FetchUsers('Company', companyId, false).catch(() => null),
        userId ? getUsersCountries(userId).catch(() => null) : Promise.resolve(null),
        getBranches(companyId).catch(() => null),
        getDepartments(companyId).catch(() => null),
      ]);
      if (cancelled) return;

      setRoster(
        (users?.payload ?? [])
          .map((u) => ({
            id: u.id,
            name: u.fullName ?? u.userName ?? '',
            countryId: u.country?.id,
            // `branchId` / `departmentId` are the flat ids; the nested views are the fallback for a
            // payload shape that ships only one of the two.
            branchId: u.branchId ?? u.branch?.id,
            departmentId: u.departmentId ?? u.department?.id,
          }))
          .filter((p) => !!p.id)
          .sort(byName),
      );
      setAllCountries(distinct((countries?.payload ?? []).map((c) => ({ id: c.id, name: c.name }))));
      setAllBranches(distinct((branches?.payload ?? []).map((b) => ({ id: b.id, name: b.name }))));
      setAllDepartments(
        distinct((departments?.payload ?? []).map((d) => ({ id: d.id, name: d.name }))),
      );
      setLoading(false);
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, enabled, reloadToken]);

  /* ── the cascade ───────────────────────────────────────────────────────────
   * Each level is narrowed by the levels ABOVE it and by nothing else, so widening a country never
   * silently drops a department the user can still reach.
   */

  const countries = allCountries;

  const branches = useMemo(() => {
    if (value.countryIds.length === 0) return allBranches;
    const reachable = distinct(
      roster
        .filter((p) => inSet(value.countryIds, p.countryId))
        .map((p) => allBranches.find((b) => b.id === p.branchId)),
    );
    // A country whose people carry no branch at all would otherwise empty the control and read as
    // "this company has no branches"; the full list is the honest fallback.
    return reachable.length > 0 ? reachable : allBranches;
  }, [allBranches, roster, value.countryIds]);

  const departments = useMemo(() => {
    if (value.countryIds.length === 0 && value.branchIds.length === 0) return allDepartments;
    const reachable = distinct(
      roster
        .filter((p) => inSet(value.countryIds, p.countryId) && inSet(value.branchIds, p.branchId))
        .map((p) => allDepartments.find((d) => d.id === p.departmentId)),
    );
    return reachable.length > 0 ? reachable : allDepartments;
  }, [allDepartments, roster, value.countryIds, value.branchIds]);

  const people = useMemo(
    () =>
      roster.filter(
        (p) =>
          inSet(value.countryIds, p.countryId) &&
          inSet(value.branchIds, p.branchId) &&
          inSet(value.departmentIds, p.departmentId),
      ),
    [roster, value.countryIds, value.branchIds, value.departmentIds],
  );

  /* ── setters, each pruning the levels below it ─────────────────────────────
   * Without the pruning a branch picked under "UAE" survives a switch to "KSA" and the request goes
   * out carrying a scope the screen no longer shows.
   */

  const setCountryIds = useCallback((countryIds: number[]) => {
    setValue((prev) => ({ ...prev, countryIds, branchIds: [], departmentIds: [], userIds: [] }));
  }, []);

  const setBranchIds = useCallback((branchIds: number[]) => {
    setValue((prev) => ({ ...prev, branchIds, departmentIds: [], userIds: [] }));
  }, []);

  const setDepartmentIds = useCallback((departmentIds: number[]) => {
    setValue((prev) => ({ ...prev, departmentIds, userIds: [] }));
  }, []);

  const setUserIds = useCallback((userIds: string[]) => {
    setValue((prev) => ({ ...prev, userIds }));
  }, []);

  const reset = useCallback(() => setValue(EMPTY_SCOPE), []);

  const hasScope =
    value.countryIds.length > 0 ||
    value.branchIds.length > 0 ||
    value.departmentIds.length > 0 ||
    value.userIds.length > 0;

  const structuralScope =
    value.countryIds.length > 0 || value.branchIds.length > 0 || value.departmentIds.length > 0;

  const resolvedUserIds = useMemo(() => {
    if (value.userIds.length > 0) return value.userIds;
    if (structuralScope) return people.map((p) => p.id);
    return [];
  }, [value.userIds, structuralScope, people]);

  const matchedNobody = hasScope && resolvedUserIds.length === 0;

  const nameOf = (options: ScopeOption[], ids: number[]) =>
    ids.map((id) => options.find((o) => o.id === id)?.name ?? String(id)).join(', ');

  const scopeText = useMemo(() => {
    if (!hasScope) return t('reports.scopeFilter.allEmployees');
    const parts: string[] = [];
    if (value.countryIds.length) parts.push(nameOf(allCountries, value.countryIds));
    if (value.branchIds.length) parts.push(nameOf(allBranches, value.branchIds));
    if (value.departmentIds.length) parts.push(nameOf(allDepartments, value.departmentIds));
    if (value.userIds.length) {
      // Three names is the point where the sentence stops being readable and starts being a list;
      // past it the COUNT is the fact the reader wanted anyway.
      parts.push(
        value.userIds.length <= 3
          ? value.userIds
              .map((id) => roster.find((p) => p.id === id)?.name ?? id)
              .join(', ')
          : t('reports.scopeFilter.peopleCount', { people: value.userIds.length }),
      );
    }
    return parts.join(' · ');
  }, [hasScope, value, allCountries, allBranches, allDepartments, roster, t]);

  return {
    value,
    setCountryIds,
    setBranchIds,
    setDepartmentIds,
    setUserIds,
    reset,
    countries,
    branches,
    departments,
    people,
    roster,
    hasScope,
    matchedNobody,
    resolvedUserIds,
    scopeText,
    loading,
    reload,
  };
}

/* ── the control ──────────────────────────────────────────────────────────── */

export interface ScopeFilterProps {
  scope: ScopeFilterState;
  disabled?: boolean;
  className?: string;
  testId?: string;
  /** Hide a level a report cannot honour. Defaults to all four. */
  levels?: ('country' | 'branch' | 'department' | 'people')[];
}

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50';
const CHIP_ACTIVE = 'border-primary bg-primary/10 text-primary';
const CHIP_IDLE =
  'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground';

const FIELD_LABEL = 'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

const ALL_LEVELS: NonNullable<ScopeFilterProps['levels']> = [
  'country',
  'branch',
  'department',
  'people',
];

export function ScopeFilter({
  scope,
  disabled = false,
  className,
  testId,
  levels = ALL_LEVELS,
}: ScopeFilterProps) {
  const { t } = useTranslation();
  const busy = disabled || scope.loading;

  const numeric = (values: unknown): number[] =>
    (Array.isArray(values) ? values : []).map((v) => Number(v)).filter((n) => Number.isFinite(n));
  const strings = (values: unknown): string[] =>
    (Array.isArray(values) ? values : []).map((v) => String(v));

  const level = (
    key: (typeof ALL_LEVELS)[number],
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
    selected: string[],
    onChange: (values: string[]) => void,
  ) =>
    levels.includes(key) ? (
      <div className="flex min-w-[11rem] flex-1 flex-col gap-1" key={key}>
        <span className={FIELD_LABEL}>{label}</span>
        <Select
          multiple
          searchable
          clearable
          disabled={busy}
          options={options}
          value={selected}
          placeholder={placeholder}
          onChange={(next: unknown) => onChange(strings(next))}
          data-testid={`scope-${key}`}
        />
      </div>
    ) : null;

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid={testId ?? 'scope-filter'}>
      <div className="flex flex-wrap items-end gap-3">
        {level(
          'country',
          t('reports.scopeFilter.countries'),
          t('reports.scopeFilter.selectCountries'),
          scope.countries.map((c) => ({ value: String(c.id), label: c.name })),
          scope.value.countryIds.map(String),
          (next) => scope.setCountryIds(numeric(next)),
        )}
        {level(
          'branch',
          t('reports.scopeFilter.branches'),
          t('reports.scopeFilter.selectBranches'),
          scope.branches.map((b) => ({ value: String(b.id), label: b.name })),
          scope.value.branchIds.map(String),
          (next) => scope.setBranchIds(numeric(next)),
        )}
        {level(
          'department',
          t('reports.scopeFilter.departments'),
          t('reports.scopeFilter.selectDepartments'),
          scope.departments.map((d) => ({ value: String(d.id), label: d.name })),
          scope.value.departmentIds.map(String),
          (next) => scope.setDepartmentIds(numeric(next)),
        )}
        {level(
          'people',
          t('reports.scopeFilter.people'),
          t('reports.scopeFilter.selectPeople'),
          scope.people.map((p) => ({ value: p.id, label: p.name })),
          scope.value.userIds,
          (next) => scope.setUserIds(next),
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          aria-pressed={!scope.hasScope}
          onClick={scope.reset}
          data-testid="scope-all"
          className={cn(CHIP_BASE, scope.hasScope ? CHIP_IDLE : CHIP_ACTIVE)}
        >
          <Users className="h-3 w-3 shrink-0" aria-hidden="true" />
          {t('reports.scopeFilter.allEmployees')}
        </button>

        {/* The scope as one sentence, in the same words the export prints on the document. */}
        {scope.hasScope && (
          <span className="min-w-0 truncate text-xs text-muted-foreground" data-testid="scope-text">
            {scope.scopeText}
          </span>
        )}

        {/* A scope that matches nobody is the one state the picker can reach that the SERVER would
            read as "everyone" — say so here rather than letting the report come back full. */}
        {scope.matchedNobody && (
          <span className="text-xs text-warning-strong" data-testid="scope-empty">
            {t('reports.scopeFilter.matchedNobody')}
          </span>
        )}
      </div>
    </div>
  );
}

export default ScopeFilter;
